import { FC, useEffect, useMemo, useState } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { COLORS } from 'src/constants/colors'
import en from 'src/constants/en'
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter'
import { getNumberOrZero } from 'src/utils/getNumberOrZero'
import {
  AnalyticsFetchSentimentAnalysisResponseData,
  AnalyticsFetchSentimentAnalysisResponseDataKeywordItem,
  AnalyticsFetchSentimentAnalysisResponseDataSourceItem,
  AnalyticsFetchThreadsIsLostType,
  KeywordFetchSentimentAnalysis,
  LanguageItem,
  MentionFormatItem,
  MentionTypeItem,
} from 'src/services/__generated__/api'
import { ReactComponent as SearchIcon } from 'src/assets/svg/searchIconGray.svg'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron.svg'
import { ReactComponent as ResetIcon } from 'src/assets/svg/rotate-left.svg'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link.svg'
import { ReactComponent as CheckIcon } from 'src/assets/svg/check-blue.svg'
import { ReactComponent as DoubleCheckIcon } from 'src/assets/svg/double-check-white.svg'
import { ReactComponent as LoudspeakerIcon } from 'src/assets/svg/loudspeaker.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import styles from './filterPanel.module.scss'

interface FilterPanelProps {
  activeTab?: 'all' | 'positive' | 'negative' | 'other'
  allKeywords?: AnalyticsFetchSentimentAnalysisResponseDataKeywordItem[]
  allLanguages?: LanguageItem[]
  allMentionFormats?: MentionFormatItem[]
  allMentionTypes?: MentionTypeItem[]
  allSources?: AnalyticsFetchSentimentAnalysisResponseDataSourceItem[]
  chartData?:
    | AnalyticsFetchSentimentAnalysisResponseData
    | KeywordFetchSentimentAnalysis
  isLost?: AnalyticsFetchThreadsIsLostType
  onApplyFilters?: (params: {
    isLost: AnalyticsFetchThreadsIsLostType
    searchText: string
    selectedKeywords: string[]
    selectedLanguages: string[]
    selectedMentionFormats: string[]
    selectedMentionTypes: string[]
    selectedSources: string[]
  }) => void
  onSearchTextChange?: (searchText: string) => void
  selectedKeywords?: string[]
  selectedLanguages?: string[]
  selectedMentionFormats?: string[]
  selectedMentionTypes?: string[]
  selectedSources?: string[]
}

type ExpandedFilter =
  | 'sources'
  | 'mentionFormat'
  | 'mentionType'
  | 'keywords'
  | 'language'
  | null

export const FilterPanel: FC<FilterPanelProps> = ({
  activeTab = 'all',
  chartData,
  allKeywords = [],
  allSources = [],
  allLanguages = [],
  allMentionFormats = [],
  allMentionTypes = [],
  selectedKeywords: initialSelectedKeywords = [],
  selectedSources: initialSelectedSources = [],
  selectedLanguages: initialSelectedLanguages = [],
  selectedMentionFormats: initialSelectedMentionFormats = [],
  selectedMentionTypes: initialSelectedMentionTypes = [],
  isLost: initialIsLost,
  onApplyFilters,
  onSearchTextChange,
}) => {
  const [searchText, setSearchText] = useState('')
  const [expandedFilter, setExpandedFilter] = useState<ExpandedFilter>(null)
  const [localSelectedSources, setLocalSelectedSources] = useState<string[]>(
    initialSelectedSources.length > 0
      ? initialSelectedSources
      : allSources.map((s) => s.source || '')
  )
  const [localSelectedMentionFormats, setLocalSelectedMentionFormats] =
    useState<string[]>(
      initialSelectedMentionFormats.length > 0
        ? initialSelectedMentionFormats
        : allMentionFormats.map((m) => m.format || '')
    )
  const [localSelectedMentionTypes, setLocalSelectedMentionTypes] = useState<
    string[]
  >(
    initialSelectedMentionTypes.length > 0
      ? initialSelectedMentionTypes
      : allMentionTypes.map((t) => t.type || '')
  )
  const [localSelectedKeywords, setLocalSelectedKeywords] = useState<string[]>(
    initialSelectedKeywords.length > 0
      ? initialSelectedKeywords
      : allKeywords.map((k) => k.keyword || '')
  )
  const [localSelectedLanguages, setLocalSelectedLanguages] = useState<
    string[]
  >(
    initialSelectedLanguages.length > 0
      ? initialSelectedLanguages
      : allLanguages.map((l) => l.language || '')
  )
  const [localIsLost, setLocalIsLost] =
    useState<AnalyticsFetchThreadsIsLostType>(
      initialIsLost || AnalyticsFetchThreadsIsLostType.False
    )

  // Track if initial data has been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)

  // Update selections when data loads (only on initial load)
  useEffect(() => {
    if (!initialDataLoaded && allSources.length > 0) {
      setLocalSelectedSources(allSources.map((s) => s.source || ''))
      setInitialDataLoaded(true)
    }
  }, [allSources, initialDataLoaded])

  useEffect(() => {
    if (
      allMentionFormats.length > 0 &&
      localSelectedMentionFormats.length === 0 &&
      !initialDataLoaded
    ) {
      setLocalSelectedMentionFormats(
        allMentionFormats.map((m) => m.format || '')
      )
    }
  }, [allMentionFormats, initialDataLoaded])

  useEffect(() => {
    if (
      allMentionTypes.length > 0 &&
      localSelectedMentionTypes.length === 0 &&
      !initialDataLoaded
    ) {
      setLocalSelectedMentionTypes(allMentionTypes.map((t) => t.type || ''))
    }
  }, [allMentionTypes, initialDataLoaded])

  useEffect(() => {
    if (
      allKeywords.length > 0 &&
      localSelectedKeywords.length === 0 &&
      !initialDataLoaded
    ) {
      setLocalSelectedKeywords(allKeywords.map((k) => k.keyword || ''))
    }
  }, [allKeywords, initialDataLoaded])

  useEffect(() => {
    if (
      allLanguages.length > 0 &&
      localSelectedLanguages.length === 0 &&
      !initialDataLoaded
    ) {
      setLocalSelectedLanguages(allLanguages.map((l) => l.language || ''))
    }
  }, [allLanguages, initialDataLoaded])

  // Apply search filter in real-time as user types (client-side only)
  useEffect(() => {
    onSearchTextChange?.(searchText)
  }, [searchText])

  // Get percentage based on active tab
  const getPercentageByTab = () => {
    switch (activeTab) {
      case 'positive':
        return getNumberOrZero(chartData?.positivePercentage)
      case 'negative':
        return getNumberOrZero(chartData?.negativePercentage)
      case 'other':
        return getNumberOrZero(chartData?.neutralPercentage)
      default:
        // For 'all' tab, show the maximum percentage
        return Math.max(
          getNumberOrZero(chartData?.positivePercentage),
          getNumberOrZero(chartData?.negativePercentage),
          getNumberOrZero(chartData?.neutralPercentage)
        )
    }
  }

  const [chartLabel, setChartLabel] = useState<number>(getPercentageByTab())

  // Update chart label when data loads or tab changes
  useEffect(() => {
    if (chartData) {
      setChartLabel(getPercentageByTab())
    }
  }, [chartData, activeTab])

  const chartOptions: EChartOption<EChartOption.SeriesPie> | undefined =
    useMemo(() => {
      if (!chartData) return undefined
      return {
        animation: false,
        color: [COLORS.malibu, COLORS.burningOrange, COLORS.grey200],
        series: [
          {
            data: [
              {
                name: en.positive,
                value:
                  (chartData as any).positives ??
                  (chartData as any).positiveMentionsCount,
              },
              {
                name: en.negative,
                value:
                  (chartData as any).negatives ??
                  (chartData as any).negativeMentionsCount,
              },
              {
                name: en.neutral,
                value:
                  (chartData as any).neutrals ??
                  (chartData as any).neutralMentionsCount,
              },
            ],
            hoverAnimation: false,
            label: { show: false },
            labelLine: { show: false },
            radius: ['60%', '85%'],
            type: 'pie',
          },
        ],
        tooltip: {
          backgroundColor: '#FFFFFF',
          borderWidth: 1,
          confine: true,
          extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);',
          formatter: (params: any) => {
            return `${params.name} : ${params.value}`
          },
          position: (point: number[]) => {
            return [point[0] + 50, point[1] + 40]
          },
          show: true,
          textStyle: {
            color: '#25262D',
            fontSize: 14,
          },
          trigger: 'item',
        },
      } as EChartOption<EChartOption.SeriesPie>
    }, [chartData])

  const handleReset = () => {
    setSearchText('')
    setLocalSelectedSources(allSources.map((s) => s.source || ''))
    setLocalSelectedMentionFormats(allMentionFormats.map((m) => m.format || ''))
    setLocalSelectedMentionTypes(allMentionTypes.map((t) => t.type || ''))
    setLocalSelectedKeywords(allKeywords.map((k) => k.keyword || ''))
    setLocalSelectedLanguages(allLanguages.map((l) => l.language || ''))
    setLocalIsLost(AnalyticsFetchThreadsIsLostType.False)
  }

  const handleSave = () => {
    onApplyFilters?.({
      isLost: localIsLost,
      searchText: searchText,
      selectedKeywords: localSelectedKeywords,
      selectedLanguages: localSelectedLanguages,
      selectedMentionFormats: localSelectedMentionFormats,
      selectedMentionTypes: localSelectedMentionTypes,
      selectedSources: localSelectedSources,
    })
  }

  const toggleFilter = (filter: ExpandedFilter) => {
    setExpandedFilter(expandedFilter === filter ? null : filter)
  }

  // Sources helpers
  const totalSourcesCount = allSources.reduce(
    (sum, s) => sum + (s.total || 0),
    0
  )
  const allSourcesSelected =
    localSelectedSources.length === allSources.length && allSources.length > 0

  const handleSelectAllSources = () => {
    if (allSourcesSelected) {
      setLocalSelectedSources([])
    } else {
      setLocalSelectedSources(allSources.map((s) => s.source || ''))
    }
  }

  const handleToggleSource = (source: string) => {
    if (localSelectedSources.includes(source)) {
      setLocalSelectedSources(localSelectedSources.filter((s) => s !== source))
    } else {
      setLocalSelectedSources([...localSelectedSources, source])
    }
  }

  // Mention Format helpers
  const totalMentionFormatsCount = allMentionFormats.reduce(
    (sum, m) => sum + (m.total || 0),
    0
  )
  const allMentionFormatsSelected =
    localSelectedMentionFormats.length === allMentionFormats.length &&
    allMentionFormats.length > 0

  const applyMentionFormats = (formats: string[]) => {
    setLocalSelectedMentionFormats(formats)
    onApplyFilters?.({
      isLost: localIsLost,
      searchText,
      selectedKeywords: localSelectedKeywords,
      selectedLanguages: localSelectedLanguages,
      selectedMentionFormats: formats,
      selectedMentionTypes: localSelectedMentionTypes,
      selectedSources: localSelectedSources,
    })
  }

  const handleSelectAllMentionFormats = () => {
    applyMentionFormats(
      allMentionFormatsSelected
        ? []
        : allMentionFormats.map((m) => m.format || '')
    )
  }

  const handleToggleMentionFormat = (format: string) => {
    applyMentionFormats(
      localSelectedMentionFormats.includes(format)
        ? localSelectedMentionFormats.filter((f) => f !== format)
        : [...localSelectedMentionFormats, format]
    )
  }

  // Mention Type helpers
  const totalMentionTypesCount = allMentionTypes.reduce(
    (sum, t) => sum + (t.total || 0),
    0
  )
  const allMentionTypesSelected =
    localSelectedMentionTypes.length === allMentionTypes.length &&
    allMentionTypes.length > 0

  const applyMentionTypes = (types: string[]) => {
    setLocalSelectedMentionTypes(types)
    onApplyFilters?.({
      isLost: localIsLost,
      searchText,
      selectedKeywords: localSelectedKeywords,
      selectedLanguages: localSelectedLanguages,
      selectedMentionFormats: localSelectedMentionFormats,
      selectedMentionTypes: types,
      selectedSources: localSelectedSources,
    })
  }

  const handleSelectAllMentionTypes = () => {
    applyMentionTypes(
      allMentionTypesSelected ? [] : allMentionTypes.map((t) => t.type || '')
    )
  }

  const handleToggleMentionType = (type: string) => {
    applyMentionTypes(
      localSelectedMentionTypes.includes(type)
        ? localSelectedMentionTypes.filter((t) => t !== type)
        : [...localSelectedMentionTypes, type]
    )
  }

  // Keywords helpers
  const totalKeywordsCount = allKeywords.reduce(
    (sum, k) => sum + (k.total || 0),
    0
  )
  const allKeywordsSelected =
    localSelectedKeywords.length === allKeywords.length &&
    allKeywords.length > 0

  const handleSelectAllKeywords = () => {
    if (allKeywordsSelected) {
      setLocalSelectedKeywords([])
    } else {
      setLocalSelectedKeywords(allKeywords.map((k) => k.keyword || ''))
    }
  }

  const handleToggleKeyword = (keyword: string) => {
    if (localSelectedKeywords.includes(keyword)) {
      setLocalSelectedKeywords(
        localSelectedKeywords.filter((k) => k !== keyword)
      )
    } else {
      setLocalSelectedKeywords([...localSelectedKeywords, keyword])
    }
  }

  // Languages helpers
  const totalLanguagesCount = allLanguages.reduce(
    (sum, l) => sum + (l.total || 0),
    0
  )
  const allLanguagesSelected =
    localSelectedLanguages.length === allLanguages.length &&
    allLanguages.length > 0

  const handleSelectAllLanguages = () => {
    if (allLanguagesSelected) {
      setLocalSelectedLanguages([])
    } else {
      setLocalSelectedLanguages(allLanguages.map((l) => l.language || ''))
    }
  }

  const handleToggleLanguage = (language: string) => {
    if (localSelectedLanguages.includes(language)) {
      setLocalSelectedLanguages(
        localSelectedLanguages.filter((l) => l !== language)
      )
    } else {
      setLocalSelectedLanguages([...localSelectedLanguages, language])
    }
  }

  return (
    <div className={styles.filterPanel}>
      <div className={styles.chartSection}>
        <div className={styles.chartContainer}>
          {chartOptions && (
            <EChart
              onMouseOut={() => setChartLabel(getPercentageByTab())}
              onMouseOver={(event) => setChartLabel(event.percent ?? 0)}
              options={chartOptions}
            />
          )}
          <div className={styles.chartLabel}>{chartLabel?.toFixed(0)}%</div>
        </div>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.positive}`} />
            <span>Positive</span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.negative}`} />
            <span>Negative</span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.neutral}`} />
            <span>Neutral</span>
          </div>
        </div>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.searchInput}>
          <SearchIcon className={styles.searchIcon} />
          <input
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by text, url or author"
            type="text"
            value={searchText}
          />
        </div>

        {/* Sources Filter */}
        <div
          className={`${styles.filterContainer} ${
            expandedFilter === 'sources' ? styles.expanded : ''
          }`}
        >
          <div
            className={styles.filterHeader}
            onClick={() => toggleFilter('sources')}
          >
            <span className={styles.filterLabel}>
              <SearchIcon className={styles.filterLabelIcon} />
              Sources
            </span>
            <span className={styles.filterValue}>
              <span className={styles.filterValueHighlight}>
                {localSelectedSources.length || 0}
              </span>
              <span className={styles.filterValueText}> Sources Selected</span>
              <ChevronIcon
                className={`${styles.chevron} ${
                  expandedFilter === 'sources' ? styles.rotated : ''
                }`}
              />
            </span>
          </div>
          {expandedFilter === 'sources' && (
            <div className={styles.filterDropdown}>
              <div
                className={`${styles.dropdownItem} ${
                  allSourcesSelected ? styles.selected : ''
                }`}
                onClick={handleSelectAllSources}
              >
                <span
                  className={`${styles.allCheckbox} ${
                    allSourcesSelected ? styles.checked : ''
                  }`}
                >
                  {allSourcesSelected && <DoubleCheckIcon />}
                </span>
                <span className={styles.dropdownItemName}>All Sources</span>
                <span className={styles.dropdownItemMeta}>
                  <span className={styles.dropdownItemDot}>•</span>
                  <span className={styles.dropdownItemCount}>
                    {totalSourcesCount.toLocaleString()}
                  </span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </span>
              </div>
              {allSources.map((sourceItem) => {
                const isSelected = localSelectedSources.includes(
                  sourceItem.source || ''
                )
                return (
                  <div
                    className={`${styles.dropdownItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    key={sourceItem.source}
                    onClick={() => handleToggleSource(sourceItem.source || '')}
                  >
                    <span
                      className={`${styles.itemCheckbox} ${
                        isSelected ? styles.checked : ''
                      }`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                    <span className={styles.dropdownItemName}>
                      {platformDisplayLabel(sourceItem.source)}
                    </span>
                    <span className={styles.dropdownItemMeta}>
                      <span className={styles.dropdownItemDot}>•</span>
                      <span className={styles.dropdownItemCount}>
                        {(sourceItem.total || 0).toLocaleString()}
                      </span>
                      <LoudspeakerIcon className={styles.loudspeakerIcon} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Mention Format Filter */}
        <div
          className={`${styles.filterContainer} ${
            expandedFilter === 'mentionFormat' ? styles.expanded : ''
          }`}
        >
          <div
            className={styles.filterHeader}
            onClick={() => toggleFilter('mentionFormat')}
          >
            <span className={styles.filterLabel}>
              <SearchIcon className={styles.filterLabelIcon} />
              Mention Format
            </span>
            <span className={styles.filterValue}>
              <span className={styles.filterValueHighlight}>
                {allMentionFormatsSelected
                  ? 'All'
                  : localSelectedMentionFormats.length}
              </span>
              <span className={styles.filterValueText}> Format Selected</span>
              <ChevronIcon
                className={`${styles.chevron} ${
                  expandedFilter === 'mentionFormat' ? styles.rotated : ''
                }`}
              />
            </span>
          </div>
          {expandedFilter === 'mentionFormat' && (
            <div className={styles.filterDropdown}>
              <div
                className={`${styles.dropdownItem} ${
                  allMentionFormatsSelected ? styles.selected : ''
                }`}
                onClick={handleSelectAllMentionFormats}
              >
                <span
                  className={`${styles.allCheckbox} ${
                    allMentionFormatsSelected ? styles.checked : ''
                  }`}
                >
                  {allMentionFormatsSelected && <DoubleCheckIcon />}
                </span>
                <span className={styles.dropdownItemName}>All Formats</span>
                <span className={styles.dropdownItemMeta}>
                  <span className={styles.dropdownItemDot}>•</span>
                  <span className={styles.dropdownItemCount}>
                    {totalMentionFormatsCount.toLocaleString()}
                  </span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </span>
              </div>
              {allMentionFormats.map((formatItem) => {
                const isSelected = localSelectedMentionFormats.includes(
                  formatItem.format || ''
                )
                return (
                  <div
                    className={`${styles.dropdownItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    key={formatItem.format}
                    onClick={() =>
                      handleToggleMentionFormat(formatItem.format || '')
                    }
                  >
                    <span
                      className={`${styles.itemCheckbox} ${
                        isSelected ? styles.checked : ''
                      }`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                    <span className={styles.dropdownItemName}>
                      {capitalizeFirstLetter(
                        formatItem.format === 'handle'
                          ? 'tag'
                          : formatItem.format || ''
                      )}
                    </span>
                    <span className={styles.dropdownItemMeta}>
                      <span className={styles.dropdownItemDot}>•</span>
                      <span className={styles.dropdownItemCount}>
                        {(formatItem.total || 0).toLocaleString()}
                      </span>
                      <LoudspeakerIcon className={styles.loudspeakerIcon} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Mention Type Filter */}
        <div
          className={`${styles.filterContainer} ${
            expandedFilter === 'mentionType' ? styles.expanded : ''
          }`}
        >
          <div
            className={styles.filterHeader}
            onClick={() => toggleFilter('mentionType')}
          >
            <span className={styles.filterLabel}>
              <SearchIcon className={styles.filterLabelIcon} />
              Mention Type
            </span>
            <span className={styles.filterValue}>
              <span className={styles.filterValueHighlight}>
                {allMentionTypesSelected
                  ? 'All'
                  : localSelectedMentionTypes.length}
              </span>
              <span className={styles.filterValueText}> Type Selected</span>
              <ChevronIcon
                className={`${styles.chevron} ${
                  expandedFilter === 'mentionType' ? styles.rotated : ''
                }`}
              />
            </span>
          </div>
          {expandedFilter === 'mentionType' && (
            <div className={styles.filterDropdown}>
              <div
                className={`${styles.dropdownItem} ${
                  allMentionTypesSelected ? styles.selected : ''
                }`}
                onClick={handleSelectAllMentionTypes}
              >
                <span
                  className={`${styles.allCheckbox} ${
                    allMentionTypesSelected ? styles.checked : ''
                  }`}
                >
                  {allMentionTypesSelected && <DoubleCheckIcon />}
                </span>
                <span className={styles.dropdownItemName}>All Types</span>
                <span className={styles.dropdownItemMeta}>
                  <span className={styles.dropdownItemDot}>•</span>
                  <span className={styles.dropdownItemCount}>
                    {totalMentionTypesCount.toLocaleString()}
                  </span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </span>
              </div>
              {allMentionTypes.map((typeItem) => {
                const isSelected = localSelectedMentionTypes.includes(
                  typeItem.type || ''
                )
                return (
                  <div
                    className={`${styles.dropdownItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    key={typeItem.type}
                    onClick={() => handleToggleMentionType(typeItem.type || '')}
                  >
                    <span
                      className={`${styles.itemCheckbox} ${
                        isSelected ? styles.checked : ''
                      }`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                    <span className={styles.dropdownItemName}>
                      {capitalizeFirstLetter(typeItem.type || '')}
                    </span>
                    <span className={styles.dropdownItemMeta}>
                      <span className={styles.dropdownItemDot}>•</span>
                      <span className={styles.dropdownItemCount}>
                        {(typeItem.total || 0).toLocaleString()}
                      </span>
                      <LoudspeakerIcon className={styles.loudspeakerIcon} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Keywords Filter */}
        <div
          className={`${styles.filterContainer} ${
            expandedFilter === 'keywords' ? styles.expanded : ''
          }`}
        >
          <div
            className={styles.filterHeader}
            onClick={() => toggleFilter('keywords')}
          >
            <span className={styles.filterLabel}>
              <SearchIcon className={styles.filterLabelIcon} />
              Keywords
            </span>
            <span className={styles.filterValue}>
              <span className={styles.filterValueHighlight}>
                {allKeywordsSelected ? 'All' : localSelectedKeywords.length}
              </span>
              <span className={styles.filterValueText}> Keywords Selected</span>
              <ChevronIcon
                className={`${styles.chevron} ${
                  expandedFilter === 'keywords' ? styles.rotated : ''
                }`}
              />
            </span>
          </div>
          {expandedFilter === 'keywords' && (
            <div className={styles.filterDropdown}>
              <div
                className={`${styles.dropdownItem} ${
                  allKeywordsSelected ? styles.selected : ''
                }`}
                onClick={handleSelectAllKeywords}
              >
                <span
                  className={`${styles.allCheckbox} ${
                    allKeywordsSelected ? styles.checked : ''
                  }`}
                >
                  {allKeywordsSelected && <DoubleCheckIcon />}
                </span>
                <span className={styles.dropdownItemName}>All Keywords</span>
                <span className={styles.dropdownItemMeta}>
                  <span className={styles.dropdownItemDot}>•</span>
                  <span className={styles.dropdownItemCount}>
                    {totalKeywordsCount.toLocaleString()}
                  </span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </span>
              </div>
              {allKeywords.map((keywordItem) => {
                const isSelected = localSelectedKeywords.includes(
                  keywordItem.keyword || ''
                )
                return (
                  <div
                    className={`${styles.dropdownItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    key={keywordItem.keyword}
                    onClick={() =>
                      handleToggleKeyword(keywordItem.keyword || '')
                    }
                  >
                    <span
                      className={`${styles.itemCheckbox} ${
                        isSelected ? styles.checked : ''
                      }`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                    <span className={styles.dropdownItemName}>
                      {keywordItem.keyword}
                    </span>
                    <span className={styles.dropdownItemMeta}>
                      <span className={styles.dropdownItemDot}>•</span>
                      <span className={styles.dropdownItemCount}>
                        {(keywordItem.total || 0).toLocaleString()}
                      </span>
                      <LoudspeakerIcon className={styles.loudspeakerIcon} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Language Filter */}
        <div
          className={`${styles.filterContainer} ${
            expandedFilter === 'language' ? styles.expanded : ''
          }`}
        >
          <div
            className={styles.filterHeader}
            onClick={() => toggleFilter('language')}
          >
            <span className={styles.filterLabel}>
              <SearchIcon className={styles.filterLabelIcon} />
              Language
            </span>
            <span className={styles.filterValue}>
              <span className={styles.filterValueHighlight}>
                {allLanguagesSelected ? 'All' : localSelectedLanguages.length}
              </span>
              <span className={styles.filterValueText}>
                {' '}
                Languages Selected
              </span>
              <ChevronIcon
                className={`${styles.chevron} ${
                  expandedFilter === 'language' ? styles.rotated : ''
                }`}
              />
            </span>
          </div>
          {expandedFilter === 'language' && (
            <div className={styles.filterDropdown}>
              <div
                className={`${styles.dropdownItem} ${
                  allLanguagesSelected ? styles.selected : ''
                }`}
                onClick={handleSelectAllLanguages}
              >
                <span
                  className={`${styles.allCheckbox} ${
                    allLanguagesSelected ? styles.checked : ''
                  }`}
                >
                  {allLanguagesSelected && <DoubleCheckIcon />}
                </span>
                <span className={styles.dropdownItemName}>All Languages</span>
                <span className={styles.dropdownItemMeta}>
                  <span className={styles.dropdownItemDot}>•</span>
                  <span className={styles.dropdownItemCount}>
                    {totalLanguagesCount.toLocaleString()}
                  </span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </span>
              </div>
              {allLanguages.map((languageItem) => {
                const isSelected = localSelectedLanguages.includes(
                  languageItem.language || ''
                )
                return (
                  <div
                    className={`${styles.dropdownItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    key={languageItem.language}
                    onClick={() =>
                      handleToggleLanguage(languageItem.language || '')
                    }
                  >
                    <span
                      className={`${styles.itemCheckbox} ${
                        isSelected ? styles.checked : ''
                      }`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                    <span className={styles.dropdownItemName}>
                      {languageItem.language}
                    </span>
                    <span className={styles.dropdownItemMeta}>
                      <span className={styles.dropdownItemDot}>•</span>
                      <span className={styles.dropdownItemCount}>
                        {(languageItem.total || 0).toLocaleString()}
                      </span>
                      <LoudspeakerIcon className={styles.loudspeakerIcon} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className={styles.urlTypesRow}>
          <span className={styles.urlTypesLabel}>
            <LinkIcon /> URL Types to Include
          </span>
          <div className={styles.urlTypesOptions}>
            <label className={styles.checkboxOption}>
              <input
                checked={
                  localIsLost === AnalyticsFetchThreadsIsLostType.False ||
                  localIsLost === AnalyticsFetchThreadsIsLostType.Both
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setLocalIsLost(AnalyticsFetchThreadsIsLostType.False)
                  }
                }}
                type="checkbox"
              />
              Functional
            </label>
            <label className={styles.checkboxOption}>
              <input
                checked={
                  localIsLost === AnalyticsFetchThreadsIsLostType.True ||
                  localIsLost === AnalyticsFetchThreadsIsLostType.Both
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setLocalIsLost(AnalyticsFetchThreadsIsLostType.True)
                  }
                }}
                type="checkbox"
              />
              Lost
            </label>
          </div>
        </div>

        <div className={styles.footerRow}>
          <button className={styles.resetBtn} onClick={handleReset}>
            <ResetIcon /> Reset Filter
          </button>

          <div className={styles.actionButtons}>
            <button className={styles.cancelBtn}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
